import { c as _c } from "react/compiler-runtime";
import { Box, BoxProps, chakra } from '@chakra-ui/react';
import { MarkdownLink } from '@src/components/sco/MarkdownRenderer/MarkdownLink';
import { MermaidDiagram } from '@src/components/sco/MarkdownRenderer/MermaidDiagram';
import { RyansTakeBlockquote } from '@src/components/sco/MarkdownRenderer/RyansTakeBlockquote';
import { ScoCitation } from '@src/components/sco/MarkdownRenderer/ScoCitation';
import { ScoOption, ScoQuestion } from '@src/components/sco/MarkdownRenderer/ScoQuestion';
import { remarkOldBlankLines } from '@src/components/sco/MarkdownRenderer/remarkOldBlankLines';
import { presets, typographyStyles } from '@src/components/sco/MarkdownRenderer/typographyStyles';
import { isValidElement, ReactNode, useMemo } from 'react';
import ReactMarkdown, { type Components, defaultUrlTransform } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

// Preserve mention:user:… deep links used in chat, while restoring react-markdown's
// default scheme stripping for everything else (blocks javascript:, data:, etc.).
const chatUrlTransform = (uri: string) => uri.startsWith('mention:') ? uri : defaultUrlTransform(uri);

// `safe` mode, for text a member wrote and a privileged viewer reads (the CRM desk): only http(s)
// and mailto links survive. Everything else (javascript:, data:, mention:, relative and
// protocol-relative URLs) becomes '' and renders as plain text, never as a link.
const SAFE_URL_PATTERN = /^(https?:|mailto:)/i;
const safeUrlTransform = (uri: string) => SAFE_URL_PATTERN.test(uri.trim()) ? defaultUrlTransform(uri.trim()) : '';

// What plain markdown can produce, plus the typing effect's wrapper. Anything else (including
// every tag that raw HTML could have introduced) is unwrapped to its text.
const SAFE_ALLOWED_ELEMENTS = ['a', 'blockquote', 'br', 'code', 'del', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'img',
// replaced by SAFE_COMPONENTS.img below, so nothing is ever fetched
'input',
// GFM task-list checkboxes (always disabled)
'li', 'ol', 'p', 'pre', 'strong', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'typing-text-node', 'ul'];
const SAFE_MARKDOWN_OPTIONS = {
  allowedElements: SAFE_ALLOWED_ELEMENTS,
  skipHtml: true,
  // raw HTML never reaches the page: no rehype-raw, and the nodes are removed
  unwrapDisallowed: true
};
const SafeMarkdownLink = t0 => {
  const $ = _c(6);
  const {
    children,
    href
  } = t0;
  if (!href) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const t1 = href.toLowerCase().startsWith("http") ? "_blank" : undefined;
  let t2;
  if ($[2] !== children || $[3] !== href || $[4] !== t1) {
    t2 = <chakra.a href={href} rel="noopener noreferrer" target={t1} title={href}>{children}</chakra.a>;
    $[2] = children;
    $[3] = href;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const SAFE_COMPONENTS: Components = {
  a: SafeMarkdownLink,
  // Images are never loaded: show the alt text, if any.
  img: ({
    alt
  }) => alt ? <span>{alt}</span> : null,
  code: ({
    className,
    children
  }) => <code className={className}>{children}</code>
};

// Recursively pull the plain text out of a React node tree (strings, arrays,
// and elements with nested `children` props).
const extractText = (node: ReactNode): string => {
  if (node == null || typeof node === 'boolean') {
    return '';
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractText).join('');
  }
  if (isValidElement(node)) {
    return extractText((node.props as {
      children?: ReactNode;
    }).children);
  }
  return '';
};

// Match "Ryan's Take" case-insensitively, tolerating straight or curly apostrophes.
const containsRyansTake = (node: ReactNode): boolean => /ryan[’']s\s+take/i.test(extractText(node));

// Strip the extra outer brackets from wiki-style links so they render as a normal link, e.g.
// `[[Foundation Model](/lexicon/foundation-model)]` -> `[Foundation Model](/lexicon/foundation-model)`
const stripWikiLinkBrackets = (value?: string): string | undefined => value?.replace(/\[(\[[^\]]*]\([^)]*\))]/g, '$1');

// Content of a blockquote line, with the leading `>` marker and one optional space removed.
const blockquoteContent = (line: string): string => line.replace(/^\s*>\s?/, '');
const isBlockquoteLine = (line: string): boolean => /^\s*>/.test(line);

// With `remarkBreaks` active, every single newline becomes a hard <br>, so a blockquote
// written as one line per sentence renders with a forced break after each. This collapses
// those single (soft) breaks within a blockquote into spaces so the quote flows as a
// paragraph, while preserving blank `>` lines as real paragraph breaks. e.g.
//   > Line one
//   > Line two
// becomes
//   > Line one Line two
const stripBlockquoteSingleBreaks = (value?: string): string | undefined => {
  if (!value) {
    return value;
  }
  const result: string[] = [];
  for (const line of value.split('\n')) {
    const prev = result[result.length - 1];
    const canMerge = prev !== undefined && isBlockquoteLine(line) && isBlockquoteLine(prev) && blockquoteContent(line).trim() !== '' && blockquoteContent(prev).trim() !== '';
    if (canMerge) {
      result[result.length - 1] = `${prev.replace(/\s+$/, '')} ${blockquoteContent(line).trim()}`;
    } else {
      result.push(line);
    }
  }
  return result.join('\n');
};
const rehypeTypingPlugin = () => (tree: any) => {
  const visit = (node: any) => {
    if (node.type === 'text' && node.value && node.value.trim().length > 0) {
      const originalValue = node.value;
      node.type = 'element';
      node.tagName = 'typing-text-node';
      node.properties = {};
      node.children = [{
        type: 'text',
        value: originalValue
      }];
      return;
    }
    if (node.children) {
      if (node.type === 'element' && (node.tagName === 'code' || node.tagName === 'pre')) {
        return;
      }
      node.children.forEach(visit);
    }
  };
  visit(tree);
};
export type ChatMessageRole = 'user' | 'assistant';
export type MarkdownRendererProps = {
  baseFontSize?: BoxProps['fontSize'];
  baseHeadingColor?: string;
  baseTextColor?: string;
  chatRole?: ChatMessageRole;
  children?: string;
  typographyStyles?: Record<string, any>;
  preset?: string;
  typingEffect?: boolean;
  delayedText?: boolean;
  boxProps?: BoxProps;
  stripWikiLinkBrackets?: boolean;
  stripBlockquoteSingleBreaks?: boolean;
  /**
   * For text written by someone else and read by a privileged viewer. Raw HTML is dropped (no
   * rehype-raw), images are never loaded, `sco-*` embeds, Mermaid and mentions do not render, and
   * links work only for http(s) and mailto. Off by default; existing callers are unaffected.
   */
  safe?: boolean;
};
export const MarkdownRenderer = props => {
  const $ = _c(36);
  let T0;
  let markdownContent;
  let rehypePlugins;
  let t0;
  let t1;
  let t2;
  if ($[0] !== props) {
    const preset = presets[props.preset] || {};
    const rolePreset = props.chatRole ? preset.roles?.[props.chatRole] : undefined;
    const baseTextColor = props.baseTextColor ?? rolePreset?.baseTextColor ?? "fg";
    let value = props.children;
    if (props.stripWikiLinkBrackets) {
      let t3;
      if ($[7] !== value) {
        t3 = stripWikiLinkBrackets(value);
        $[7] = value;
        $[8] = t3;
      } else {
        t3 = $[8];
      }
      value = t3;
    }
    if (props.stripBlockquoteSingleBreaks) {
      let t3;
      if ($[9] !== value) {
        t3 = stripBlockquoteSingleBreaks(value);
        $[9] = value;
        $[10] = t3;
      } else {
        t3 = $[10];
      }
      value = t3;
    }
    markdownContent = value;
    let t3;
    if ($[11] !== props.safe) {
      t3 = props.safe ? [] : [rehypeRaw];
      $[11] = props.safe;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    let t4;
    if ($[13] !== props.typingEffect) {
      t4 = props.typingEffect ? [rehypeTypingPlugin] : [];
      $[13] = props.typingEffect;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    let t5;
    if ($[15] !== t3 || $[16] !== t4) {
      t5 = [...t3, ...t4];
      $[15] = t3;
      $[16] = t4;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    rehypePlugins = t5;
    T0 = Box;
    t1 = "longform";
    t2 = props.baseFontSize;
    t0 = typographyStyles({
      ...(preset.typographyStyles || {}),
      ...(rolePreset?.typographyStyles || {}),
      ...(props.typographyStyles || {})
    }, {
      baseTextColor
    });
    $[0] = props;
    $[1] = T0;
    $[2] = markdownContent;
    $[3] = rehypePlugins;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    T0 = $[1];
    markdownContent = $[2];
    rehypePlugins = $[3];
    t0 = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  let t3;
  if ($[18] !== t0) {
    t3 = {
      ...t0
    };
    $[18] = t0;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [remarkGfm, remarkOldBlankLines, remarkBreaks];
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const t5 = props.safe ? safeUrlTransform : chatUrlTransform;
  const t6 = props.safe ? SAFE_MARKDOWN_OPTIONS : null;
  let t7;
  if ($[21] !== props.safe) {
    t7 = props.safe ? SAFE_COMPONENTS : {
      a: MarkdownLink,
      code: _temp,
      blockquote: _temp2,
      "sco-citation": ScoCitation,
      "sco-question": ScoQuestion,
      "sco-option": ScoOption
    };
    $[21] = props.safe;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== markdownContent || $[24] !== rehypePlugins || $[25] !== t5 || $[26] !== t6 || $[27] !== t7) {
    t8 = <ReactMarkdown rehypePlugins={rehypePlugins} remarkPlugins={t4} urlTransform={t5} {...t6} components={t7}>{markdownContent}</ReactMarkdown>;
    $[23] = markdownContent;
    $[24] = rehypePlugins;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== T0 || $[30] !== props.boxProps || $[31] !== t1 || $[32] !== t2 || $[33] !== t3 || $[34] !== t8) {
    t9 = <T0 fontFamily={t1} fontSize={t2} css={t3} {...props.boxProps}>{t8}</T0>;
    $[29] = T0;
    $[30] = props.boxProps;
    $[31] = t1;
    $[32] = t2;
    $[33] = t3;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  return t9;
};
function _temp(t0) {
  const {
    className,
    children
  } = t0;
  if (className === "language-mermaid") {
    return <MermaidDiagram code={String(children).trimEnd()} />;
  }
  return <code className={className}>{children}</code>;
}
function _temp2(blockquote) {
  if (containsRyansTake(blockquote.children)) {
    return <RyansTakeBlockquote>{blockquote.children}</RyansTakeBlockquote>;
  }
  return <blockquote className={blockquote.className}>{blockquote.children}</blockquote>;
}